// src/components/CreateCompanyAddressModal.jsx

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";

import { CREATE_COMPANY_ADDRESS } from "../graph/companyAddresses/mutations.js";
import useGraphQLClient from "../hooks/useGraphQLClient.js";
import { useSnackbar } from "./snackbarProvider.jsx";

const validationSchema = yup.object().shape({
  addressLine1: yup.string().trim().required("Address line 1 is required"),
  addressLine2: yup.string().nullable(),
  town: yup.string().nullable(),
  county: yup.string().nullable(),
  postcode: yup.string().nullable(),
  country: yup.string().trim().required("Country is required"),
  latitude: yup
    .number()
    .typeError("Latitude must be a number")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90")
    .required("Latitude is required for the map"),
  longitude: yup
    .number()
    .typeError("Longitude must be a number")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180")
    .required("Longitude is required for the map"),
  isPrimary: yup.boolean(),
});

const floatOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;

  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const CreateCompanyAddressModal = ({
  open,
  companyID,
  onClose,
  onCreated,
}) => {
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();

  const initialValues = {
    label: "Primary",
    addressLine1: "",
    addressLine2: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    latitude: "",
    longitude: "",
    isPrimary: true,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const input = {
        companyID,
        label: values.label || null,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2 || null,
        town: values.town || null,
        county: values.county || null,
        postcode: values.postcode || null,
        country: values.country,
        latitude: floatOrNull(values.latitude),
        longitude: floatOrNull(values.longitude),
        isPrimary: values.isPrimary,
      };

      await client.request(CREATE_COMPANY_ADDRESS, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address created successfully",
      });

      onCreated?.();
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("Error creating company address:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: `Failed to create address: ${message}`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Company Address</DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  label="Label"
                  name="label"
                  value={values.label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.label && !!errors.label}
                  helperText={touched.label && errors.label}
                  sx={{ gridColumn: "span 2" }}
                />

                <Box sx={{ gridColumn: "span 2" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isPrimary}
                        onChange={(event) =>
                          setFieldValue("isPrimary", event.target.checked)
                        }
                      />
                    }
                    label="Primary address"
                  />
                </Box>

                <TextField
                  label="Address Line 1"
                  name="addressLine1"
                  value={values.addressLine1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.addressLine1 && !!errors.addressLine1}
                  helperText={touched.addressLine1 && errors.addressLine1}
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  label="Address Line 2"
                  name="addressLine2"
                  value={values.addressLine2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.addressLine2 && !!errors.addressLine2}
                  helperText={touched.addressLine2 && errors.addressLine2}
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  label="Town / City"
                  name="town"
                  value={values.town}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.town && !!errors.town}
                  helperText={touched.town && errors.town}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  label="County"
                  name="county"
                  value={values.county}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.county && !!errors.county}
                  helperText={touched.county && errors.county}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  label="Postcode"
                  name="postcode"
                  value={values.postcode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.postcode && !!errors.postcode}
                  helperText={touched.postcode && errors.postcode}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  label="Country"
                  name="country"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.country && !!errors.country}
                  helperText={touched.country && errors.country}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  label="Latitude"
                  name="latitude"
                  type="number"
                  value={values.latitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.latitude && !!errors.latitude}
                  helperText={
                    touched.latitude && errors.latitude
                      ? errors.latitude
                      : "Required for contact map"
                  }
                  inputProps={{ step: "any" }}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  label="Longitude"
                  name="longitude"
                  type="number"
                  value={values.longitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.longitude && !!errors.longitude}
                  helperText={
                    touched.longitude && errors.longitude
                      ? errors.longitude
                      : "Required for contact map"
                  }
                  inputProps={{ step: "any" }}
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} variant="outlined">
                Skip For Now
              </Button>

              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting || !companyID}
              >
                Create Address
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateCompanyAddressModal;